import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useCookies } from 'react-cookie';

import API from '@/api';
import { sortClientsAscendingByDate } from '@/helpers';
import { createSuspenseQueryHook } from '@/helpers/create-query-hook';
import { COOKIE_KEYS } from '@/constants';

const SESSION_QUERY_KEY = 'session';

const useSessionQuery = createSuspenseQueryHook(
  (accessToken: string) => [SESSION_QUERY_KEY, accessToken],
  async accessToken => {
    // Get workspace data from localStorage
    const workspaceData = localStorage.getItem('workspace');

    // if the url has a client id, use that client id
    const url = new URL(window.location.href);
    const clientId = url.pathname.split('/')[2];
    // if clientid , then set the localStorage client id to that client id
    if (!Number.isNaN(Number(clientId))) {
      const existingWorkspace = JSON.parse(
        localStorage.getItem('workspace') || '{}'
      );
      localStorage.setItem(
        'workspace',
        JSON.stringify({
          ...existingWorkspace,
          client: clientId,
        })
      );
    }

    const [userResponse, businessResponse] = await Promise.all([
      API.user.get(),
      API.business.find({ fields: 'billing' }),
    ]);

    const user = userResponse?.data?.[0];
    const userId = user?.id;
    const availableBusinesses = businessResponse?.data;
    const planResponse = businessResponse?.paymentPlans;

    let paymentPlans;
    if (planResponse && Object.keys(planResponse).length) {
      paymentPlans = planResponse;
    }

    // Parse workspace data if exists
    let savedWorkspace;
    try {
      savedWorkspace = workspaceData ? JSON.parse(workspaceData) : null;
    } catch (error) {
      console.error('[useSessionQuery] Error parsing workspace data:', error);
    }

    const primaryBusiness = businessResponse?.data?.find(
      (b: { access: { isPrimary: boolean } }) => b.access?.isPrimary
    );

    const business = savedWorkspace?.businessId
      ? availableBusinesses?.find(b => b.id === savedWorkspace.businessId)
      : primaryBusiness;

    const businessId = business?.id;

    if (business && !!userId && !!businessId) {
      const [
        clientsResponse,
        permissionResponse,
        pagesResponse,
        billingResponse,
      ] = await Promise.all([
        API.clients.find({
          businessId,
          fields: 'accounts',
          $limit: 50,
        }),
        API.permission.find({
          userId,
        }),
        API.accounts.find({
          businessId,
          fields: 'provider',
          paginate: false,
        }),
        API.billingInfo.find({ businessId }),
      ]);

      const permissions = permissionResponse;

      const permission = permissions?.find?.(
        (p: { businessId: string }) =>
          String(p.businessId) === String(businessId)
      );

      const clients = clientsResponse.data.map(
        (client: {
          id: string;
          accounts: { provider: { type: string } }[];
        }) => ({
          ...client,
          accounts: client.accounts.map(account => ({
            service: account.provider.type,
            ...account,
          })),
        })
      );

      const pages = pagesResponse;
      const billing = billingResponse;

      // Select client based on localStorage data or url
      const selectedClient = savedWorkspace?.client
        ? clients.find(
            (c: { id: string }) =>
              String(c.id) === String(savedWorkspace.client)
          )
        : sortClientsAscendingByDate(clients)[0];

      return {
        user,
        client: { ...selectedClient, id: selectedClient?.id.toString() },
        business,
        clients,
        permission,
        permissions,
        paymentPlans,
        availableBusinesses,
        businessOptions: availableBusinesses,
        workspaceData: savedWorkspace, // Include workspace data in the response
        pages,
        billing,
      };
    }

    return {
      user,
      client: null,
      business: null,
      clients: [],
      permission: null,
      permissions: [],
      paymentPlans: null,
      availableBusinesses: [],
      businessOptions: [],
      workspaceData: savedWorkspace,
      billing: null,
    };
  }
);

const useSession = () => {
  const [cookieValues] = useCookies([COOKIE_KEYS.ACCESS_TOKEN]);
  return useSessionQuery(cookieValues.accessToken);
};

const useBusiness = () => {
  const [cookieValues] = useCookies([COOKIE_KEYS.ACCESS_TOKEN]);
  const query = useSessionQuery(cookieValues.accessToken, {
    select: data => data.business as { id: string },
  });
  return query.data;
};

const useClient = () => {
  const [cookieValues] = useCookies([COOKIE_KEYS.ACCESS_TOKEN]);

  const query = useSessionQuery(cookieValues.accessToken, {
    select: data => {
      const selectedClient = data.client as { id: string };
      return selectedClient;
    },
  });
  return query.data;
};

/**
 * Invalidate all of the session queries
 * @returns
 */
const useRefreshSession = () => {
  const queryClient = useQueryClient();
  return useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: [SESSION_QUERY_KEY],
    });
  }, [queryClient]);
};

export { useBusiness, useClient, useRefreshSession, useSession };
